import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Col,
  Form,
  Grid,
  Input,
  InputNumber,
  Popover,
  Radio,
  Row,
  Select,
  Skeleton,
  Space,
  Statistic,
  Steps,
  Switch,
  Typography
} from 'antd';
import Icon, {
  DollarCircleOutlined,
  EditOutlined,
  EnvironmentOutlined,
  ExclamationCircleOutlined,
  FlagOutlined,
  InfoCircleOutlined,
  PhoneOutlined,
  PlusOutlined,
  RollbackOutlined,
  ShopOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { useForm } from 'antd/lib/form/Form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { userSelector } from '../../redux/selectors/user';
import Paper from '../../components/Paper/Paper';
import { iconActiveRoundConfig, iconDisabledRoundConfig } from '../../styles/theme/configs';
import { searchForLocations } from '../../api/locations';
import { DrivingDistanceRoutesTransformed, Location, SearchLocation } from '../../api/models/locations';
import debounce from 'lodash/debounce';
import { getDropOffLocationTitle, getPickupLocationTitle } from '../../utils/deliveries';
import styles from './CreateDeliveryFormV2.module.less';
import { Button } from '../../components/Button/Button';
import { whenOrderIsCreated, whenOrderIsFailed, whenOrderIsQueued } from './messages';
import { getErrorFromResponse, NetworkError } from '../../utils/apiHelpers';
import { WalletError } from './walletErrors';
import i18next from '../../i18next';
import { RuleObject, ValidateErrorEntity } from 'rc-field-form/lib/interface';
import Map from '../../components/Map';
import { loadingDeliveriesEffectsSelector, loadingUserEffectsSelector } from '../../redux/selectors/loading';
import AddLocationModal from '../../components/AddLocationModal/AddLocationModal';
import { AppDispatch } from '../../types/store';
import {
  addNewAddressClicked,
  contactPhoneNumberValidationError,
  customerPhoneNumberValidationError,
  dropOffLocationChanged,
  emptyContactPhoneNumberError,
  emptyCustomerPhoneNumberError,
  pickupLocationChanged,
  requestDeliveryClicked,
  requestDeliveryFormValidationFailed,
  requestDeliveryPaymentMethodFailed,
  requestDeliveryScreenOpen,
  requestDeliverySubmitFailed
} from '../../utils/analytika/events';
import { ANALYTIKA_ACTIONS, ANALYTIKA_EVENTS_V2, ANALYTIKA_SCREENS } from '../../utils/analytika/enums';
import { deliveriesSelector } from '../../redux/selectors/deliveries';
import AddDropoffModal, { AddDropoffFormValues } from '../../components/AddDropoffModal';
import {
  displayDeliveryTimeslotDate,
  displayDeliveryTimeslotTime,
  getLocationSelectOptions
} from '../../utils/componentHelpers';
import Banner from '../../components/Banner/Banner';
import Overlay from '../../components/Map/Overlay';
import PhoneNumberAntd from '../../components/PhoneNumber/PhoneNumberAntd';
import { DeliveryAction, DeliveryPriceEstimation, PaymentOnDeliveryType } from '../../api/models/deliveries';
import DoorstepIcon from '../../assets/img/doorstep.svg?react';
import CashIcon from '../../assets/img/cash.svg?react';
import CreditCardIcon from '../../assets/img/creditCard.svg?react';
import PencilIcon from '../../assets/img/pencilIcon.svg?react';
import { colorIcon, colorInfoBorder, colorTextDisabled } from '../../styles/theme';
import { MapUseCase } from '../../components/Map/Map';
import publishAV2Event from '../../utils/analytika/reporterAnalytikaV2';
import CustomCard from '../../components/CustomCTACard';
import { FeatureFlag } from '../../utils/galileo/featureFlag.enum';
import DeliveryOptionDrawer from '../../components/DeliveryOptionDrawer';
import { timeslotsSelector } from '../../redux/selectors/timeslots';
import { DeliveryOption } from '../../types';
import { TimeSlotWithDate } from '../../api/models/timeslot';

const { useBreakpoint } = Grid;

export type CreateDeliveryFormv2Props = {
  isBlocked: boolean;
  isFirstDeliveryScreen?: boolean;
};

type DeliveryFormValues = {
  contactPhoneNumber: { number: string; fullNumber: string };
  customerPhoneNumber?: { number: string; fullNumber: string };
  pickupLocation?: string;
  cashOnDelivery?: number;
  deliveryDetails?: string;
  dropOffLocation?: string;
  customerName?: string;
  orderId?: string;
  deliveryAction?: DeliveryAction;
  buildingName?: string;
  paymentMethodOnDelivery?: PaymentOnDeliveryType;
  deliveryTimeSlot?: TimeSlotWithDate;
};

const overlay = (
  <Overlay customIcon={() => <EnvironmentOutlined />} text={i18next.t('deliveries.mapCaption.addPickupAddress')} />
);

const initialEstimatedPrice: DeliveryPriceEstimation = { currency: '', tripCost: 0 };

const CreateDeliveryFormv2 = ({ isBlocked, isFirstDeliveryScreen }: CreateDeliveryFormv2Props) => {
  const [isDropoffSearchLoading, setIsDropoffSearchLoading] = useState(false);
  const [drivingDirections, setDrivingDirections] = useState<DrivingDistanceRoutesTransformed | null>(null);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [isDropoffValid, setIsDropoffValid] = useState(false);
  const [dropOffLocations, setDropoffLocations] = useState<SearchLocation[]>([]);
  const [isAddLocationModalVisible, setAddLocationModalVisible] = useState(false);
  const [isAddDropOffModalVisible, setAddDropOffModalVisible] = useState(false);
  const [isPickupDropdownOpen, setPickupDropdownOpen] = useState(false);
  const [isLeaveAtDoorstepDisabled, setIsLeaveAtDoorstepDisabled] = useState(false);
  const [isPaymentOnDeliveryRequired, setIsPaymentOnDeliveryRequired] = useState(false);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [estimatedPrice, setEstimatedPrice] = useState<DeliveryPriceEstimation>(initialEstimatedPrice);
  const [selectedDeliveryOption, setSelectedDeliveryOption] = useState<DeliveryOption>(DeliveryOption.INSTANT);
  const [form] = useForm<DeliveryFormValues>();
  const { t } = useTranslation();
  const {
    pickupLocations,
    cities,
    merchantConfig: {
      isDropOffMandatory,
      preferredContactNumber,
      isCustomerPhoneNumberMandatory,
      customFulfillmentDistance,
      isCustomFulfillmentDistanceActive,
      cfdEndsAt,
      isOrderIdMandatory,
      defaultDeliveryAction,
      isCCODAllowed
    },
    defaultPaymentMethodId,
    isWalletCreditUsed,
    secondaryPaymentMethodId,
    userWalletConfig: {
      userBalance: { currency, fractionDigits }
    },
    userData,
    defaultPickupLocation,
    paymentMethods,
    userGalileoVars: {
      [FeatureFlag.IS_CCOD_ENABLED]: isCCODEnabled,
      [FeatureFlag.IS_SCHEDULED_ENABLED]: isScheduledDeliveriesEnabled,
      [FeatureFlag.IS_ESTIMATES_ENABLED]: isEstimatesEnabled,
      [FeatureFlag.IS_ZONE_VALIDATION_ENABLED]: isZoneValidationEnabled
    }
  } = useSelector(userSelector);

  const userCity = userData?.city;

  const userContactNumber = preferredContactNumber || (userData && userData.phone);

  const formInitValues: DeliveryFormValues = {
    pickupLocation: defaultPickupLocation ? `${defaultPickupLocation.id}` : undefined,
    contactPhoneNumber: { number: userContactNumber, fullNumber: `+${userContactNumber}` },
    dropOffLocation: undefined,
    cashOnDelivery: undefined,
    customerPhoneNumber: undefined,
    orderId: undefined,
    deliveryDetails: undefined,
    customerName: undefined,
    deliveryAction: defaultDeliveryAction,
    buildingName: undefined,
    paymentMethodOnDelivery: isCCODEnabled ? PaymentOnDeliveryType.CASH : undefined
  };

  const { activeDeliveries } = useSelector(deliveriesSelector);

  const { timeSlotsByDate, isTimeSlotsFetching } = useSelector(timeslotsSelector);

  const { fetchPickupLocations: isPickupLocationsLoading } = useSelector(loadingUserEffectsSelector);
  const { getDeliveryPriceEstimation: isPriceEstimationLoading } = useSelector(loadingDeliveriesEffectsSelector);

  const {
    deliveries: { createDelivery, getDeliveryPriceEstimation, getDeliveryZoneValidation },
    user: { storeSecondaryPaymentMethod, publishAnalytikaEvent, selectPickupLocation },
    timeslots: { fetchTimeslots }
  } = useDispatch<AppDispatch>();

  useEffect(() => {
    defaultPickupLocation && form.setFields([{ name: 'pickupLocation', value: defaultPickupLocation?.id }]);
  }, [isPickupLocationsLoading, defaultPickupLocation]);

  const phoneNumberValidator = useMemo(
    () =>
      async (rule: RuleObject, value: { number: string; fullNumber: string } = { number: '', fullNumber: '' }) => {
        const { fullNumber, number: numberEntered } = value;
        // TODO: For some reason, antD didn't finalize typings
        //@ts-ignore
        const { field: fieldName, required: isFieldRequired } = rule;
        if (numberEntered) {
          if (!isValidPhoneNumber(fullNumber)) {
            const errEvent = { phone_number: fullNumber };
            publishAnalytikaEvent(
              fieldName === 'customerPhoneNumber'
                ? customerPhoneNumberValidationError(errEvent)
                : contactPhoneNumberValidationError(errEvent)
            );

            publishAV2Event({
              event_name:
                fieldName === 'customerPhoneNumber'
                  ? ANALYTIKA_EVENTS_V2.EXPR_INVALID_CUST_PHONE
                  : ANALYTIKA_EVENTS_V2.EXPR_INVALID_CONTACT_PHONE,
              event_version: 2,
              entered_phone_number: fullNumber
            });
            return Promise.reject(i18next.t(`deliveries.${fieldName}.numbersOnly`));
          }
        } else if (isFieldRequired) {
          publishAnalytikaEvent(
            fieldName === 'customerPhoneNumber' ? emptyCustomerPhoneNumberError() : emptyContactPhoneNumberError()
          );
          publishAV2Event({
            event_name:
              fieldName === 'customerPhoneNumber'
                ? ANALYTIKA_EVENTS_V2.EXPR_ACKNOWLEDGE_MISS_CUST_PHONE
                : ANALYTIKA_EVENTS_V2.EXPR_ACKNOWLEDGE_MISS_USER_PHONE,
            event_version: 2
          });
          return Promise.reject(i18next.t(`deliveries.${fieldName}.required`));
        }
        return Promise.resolve();
      },
    [publishAnalytikaEvent]
  );

  const { md: isMdBreakpoint, sm: isSmBreakpoint } = useBreakpoint();

  let isoCountryName;
  try {
    const { country } = parsePhoneNumber(`+${userContactNumber}`);
    isoCountryName = country;
  } catch (err) {
    isoCountryName = cities.find(city => city.id === pickupCityId)?.country.iso2.toLowerCase();
  }

  const onChangePickupLocation = (id: string | number) => {
    const pickupLocationObject = pickupLocations.find(location => +location.id === +id);
    selectPickupLocation(pickupLocationObject);
    form.resetFields(['dropOffLocation', 'buildingName']);
    setDrivingDirections(null);
    setEstimatedPrice(initialEstimatedPrice);
    setSelectedDeliveryOption(DeliveryOption.INSTANT);
    setDropoffLocations([]);
    setIsDropoffValid(false);
    publishAnalytikaEvent(pickupLocationChanged({ pick_up_location_id: id }));
    publishAV2Event({
      event_name: ANALYTIKA_EVENTS_V2.EXPR_UPDATE_PICKUP,
      event_version: 2,
      location_id: `${id}`,
      pickup_latitude: pickupLocationObject?.location.lat,
      pickup_longitude: pickupLocationObject?.location.lng,
      update_id: pickupLocationObject?.nickname
    });
  };

  const postOrderCreation = async (error?: Error): Promise<void> => {
    if (!error) {
      publishAnalytikaEvent(
        requestDeliveryScreenOpen(
          { active_deliveries_count: activeDeliveries.length },
          ANALYTIKA_ACTIONS.REQUEST_SUBMITTED
        )
      );
      form.resetFields();
      setIsDropoffValid(false);
      whenOrderIsCreated();
      defaultPickupLocation && form.setFieldValue('pickupLocation', defaultPickupLocation.id);
    } else if (error instanceof NetworkError) {
      form.resetFields();
      whenOrderIsQueued();
    } else if (error instanceof WalletError) {
      form.resetFields();
    } else {
      whenOrderIsFailed(getErrorFromResponse(error));
    }
    setIsPaymentOnDeliveryRequired(false);
    setSelectedDeliveryOption(DeliveryOption.INSTANT);
    setEstimatedPrice(initialEstimatedPrice);
  };

  useEffect(() => {
    form.validateFields(['customerName', 'customerPhoneNumber']);
  }, [selectedDeliveryOption]);

  const selectedPickupLocationId = Form.useWatch<string>('pickupLocation', form);
  const usersBuildingName = form.getFieldValue('buildingName'); // useWatch didn't work here

  const pickupCityId = useMemo(
    () =>
      selectedPickupLocationId && pickupLocations.find(location => +location.id === +selectedPickupLocationId)?.city_id,
    [selectedPickupLocationId]
  );

  const pickupLocation = useMemo<Location | undefined>(
    () => pickupLocations.find(location => +location.id === +selectedPickupLocationId),
    [selectedPickupLocationId, pickupLocations]
  );

  const deliveryZoneValidator = useMemo(
    () => async () => {
      if (isZoneValidationEnabled && selectedDeliveryOption === DeliveryOption.INSTANT && pickupLocation) {
        try {
          const isZoneValid = await getDeliveryZoneValidation(pickupLocation.location);

          return isZoneValid ? Promise.resolve() : Promise.reject(t('deliveries.pickupLocation.zoneValidationFailed'));
        } catch (err) {
          // Gracefully handle the error as it is caught in axios interceptor
        }
      }
      return Promise.resolve();
    },
    [isZoneValidationEnabled, pickupLocation, selectedDeliveryOption]
  );

  const selectedDropoffLocationId = Form.useWatch<string>('dropOffLocation', form);
  const dropOffLocation = useMemo<SearchLocation | undefined>(() => {
    return dropOffLocations.find(location => location.id === selectedDropoffLocationId);
  }, [selectedDropoffLocationId, dropOffLocations]);

  useEffect(() => {
    if ((isScheduledDeliveriesEnabled || isEstimatesEnabled) && pickupLocation && dropOffLocation) {
      getDeliveryPriceEstimation({
        pickupLocation: pickupLocation?.location,
        dropOffLocation: dropOffLocation.location
      }).then((fetchedEstimatedPrice: DeliveryPriceEstimation) => {
        setEstimatedPrice(fetchedEstimatedPrice);
      });
    }
  }, [isScheduledDeliveriesEnabled, isEstimatesEnabled, pickupLocation, dropOffLocation]);

  const onDropoffSearchStart = debounce(async (query: string | undefined) => {
    if (!query || !pickupLocation) {
      return;
    }
    const { location: latLng } = pickupLocation;
    setIsDropoffSearchLoading(true);
    const fetchedDropoffLocations = await searchForLocations(query, latLng);
    setDropoffLocations(fetchedDropoffLocations);
    setIsDropoffSearchLoading(false);
  }, 300);

  const pickupLocationSelectOptions = useMemo(
    () => getLocationSelectOptions(pickupLocations, getPickupLocationTitle, undefined, 'pickupLocationItem'),
    [pickupLocations]
  );

  const dropoffLocationSelectOptions = useMemo(
    () => getLocationSelectOptions(dropOffLocations, getDropOffLocationTitle),
    [dropOffLocations]
  );

  const openAddDropoffModal = () => {
    setAddDropOffModalVisible(true);
  };

  const onDropOffMarkerChange = openAddDropoffModal;

  const onSubmitModal = (
    dropoffModalFields: AddDropoffFormValues,
    directions: DrivingDistanceRoutesTransformed | null,
    dropoffLocation: SearchLocation
  ) => {
    const { dropOffId, buildingName } = dropoffModalFields;
    setDropoffLocations([dropoffLocation]);
    setDrivingDirections(directions);
    form.setFieldValue('dropOffLocation', dropOffId);
    form.setFieldValue('buildingName', buildingName);
    form.validateFields(['dropOffLocation', 'buildingName', 'deliveryDetails']);
    setIsDropoffValid(true);
    publishAnalytikaEvent(dropOffLocationChanged({ drop_off_location_id: dropOffId }));
  };

  const selectedDeliveryTimeSlot: TimeSlotWithDate | undefined = form.getFieldValue('deliveryTimeSlot');

  const onFinish = async (fields: DeliveryFormValues) => {
    if (!pickupLocation) {
      return;
    }
    setIsFormSubmitting(true);
    try {
      const {
        cashOnDelivery,
        dropOffLocation: dropOffLocationId,
        customerPhoneNumber: customerPhoneNumberField,
        contactPhoneNumber: contactPhoneNumberField,
        deliveryDetails,
        customerName,
        orderId,
        deliveryAction,
        buildingName,
        paymentMethodOnDelivery
      } = fields;

      const { fullNumber: userPhoneNumber } = contactPhoneNumberField || {};
      const { fullNumber: customerPhoneNumber } = customerPhoneNumberField || {};

      const dropOffLocationSelected = dropOffLocations.find(dropoffItem => dropOffLocationId === dropoffItem.id);

      const backupCreditCardMethodId = paymentMethods.find(
        paymentMethod => paymentMethod.paymentType === 'credit_card'
      )?.id;

      let paymentMethodId;

      if (!isWalletCreditUsed && defaultPaymentMethodId) {
        paymentMethodId = defaultPaymentMethodId;
      }

      if (isWalletCreditUsed && secondaryPaymentMethodId) {
        paymentMethodId = secondaryPaymentMethodId;
      }

      if (isWalletCreditUsed && backupCreditCardMethodId && !secondaryPaymentMethodId) {
        paymentMethodId = backupCreditCardMethodId;
        storeSecondaryPaymentMethod(backupCreditCardMethodId);
      }

      publishAnalytikaEvent(
        requestDeliveryClicked({
          pick_up_location: pickupLocation?.id,
          pick_up_lat_long: pickupLocation?.location,
          drop_off_location: dropOffLocation?.id,
          drop_off_lat_long: dropOffLocation?.location,
          order_details: {
            cash_to_be_collected: cashOnDelivery,
            customer_name: customerName,
            customer_number: customerPhoneNumber,
            payment_id: paymentMethodId,
            order_id: orderId,
            use_credit: Boolean(isWalletCreditUsed),
            captain_notes: deliveryDetails
          },
          delivery_action: deliveryAction,
          payment_on_delivery: paymentMethodOnDelivery
        })
      );
      publishAV2Event({
        event_name: ANALYTIKA_EVENTS_V2.EXPR_CLICK_DEL_REQ_FORM,
        event_version: 6,
        cash_to_be_collected: cashOnDelivery,
        customer_name: customerName,
        customer_number: customerPhoneNumber,
        payment_id: paymentMethodId,
        order_details: orderId,
        use_credit: Boolean(isWalletCreditUsed),
        captain_notes: deliveryDetails,
        delivery_date: isScheduledDeliveriesEnabled ? selectedDeliveryTimeSlot?.date : '',
        delivery_instruction: deliveryAction,
        delivery_time_slot: isScheduledDeliveriesEnabled
          ? `${selectedDeliveryTimeSlot?.from} - ${selectedDeliveryTimeSlot?.to}`
          : '',
        delivery_type: selectedDeliveryTimeSlot ? DeliveryOption.SCHEDULED : DeliveryOption.INSTANT,
        dropoff_latitude: dropOffLocation?.location.lat,
        dropoff_longitude: dropOffLocation?.location.lng,
        pickup_latitude: pickupLocation?.location.lat,
        pickup_longitude: pickupLocation?.location.lng,
        fare_estimate: Number(estimatedPrice.tripCost) || undefined,
        value: estimatedPrice.currency
      });

      if (!paymentMethodId) {
        whenOrderIsFailed(t('deliveries.missingPaymentMethod'));
        publishAV2Event({
          event_name: ANALYTIKA_EVENTS_V2.EXPR_FAIL_PAYMENT,
          event_version: 6,
          order_details: orderId,
          dropoff_location: dropOffLocation?.formattedAddress || dropOffLocation?.name || dropOffLocation?.building,
          pickup_location: pickupLocation?.nickname || pickupLocation?.name || pickupLocation?.area,
          value: JSON.stringify(fields)
        });
        publishAnalytikaEvent(
          requestDeliveryPaymentMethodFailed({
            pick_up_location: pickupLocation?.id,
            pick_up_lat_long: pickupLocation?.location,
            drop_off_location: dropOffLocation?.id,
            drop_off_lat_long: dropOffLocation?.location,
            order_details: {
              cash_to_be_collected: cashOnDelivery,
              customer_name: customerName,
              customer_number: customerPhoneNumber,
              payment_id: paymentMethodId,
              order_id: orderId,
              use_credit: Boolean(isWalletCreditUsed),
              captain_notes: deliveryDetails
            },
            delivery_action: deliveryAction
          })
        );
        return;
      }

      await createDelivery({
        pickupLocation,
        dropOffLocation: dropOffLocationSelected,
        paymentMethodId,
        deliveryDetails,
        cashOnDelivery,
        customerPhoneNumber,
        customerName,
        useCredit: isWalletCreditUsed,
        userPhoneNumber,
        orderId,
        deliveryAction,
        buildingName,
        // TODO: remove condition after rollout 100%
        paymentMethodOnDelivery: isCCODEnabled ? paymentMethodOnDelivery : undefined,
        deliveryWindow: isScheduledDeliveriesEnabled ? selectedDeliveryTimeSlot : undefined
      });
      await postOrderCreation();
    } catch (err) {
      publishAnalytikaEvent(requestDeliverySubmitFailed({ error: err }));
      whenOrderIsFailed(getErrorFromResponse(err));
      publishAV2Event({
        event_name: ANALYTIKA_EVENTS_V2.EXPR_FAIL_DEL_REQ_FORM,
        event_version: 10,
        value: `BACKEND_ERROR: ${err?.message}`,
        cash_to_be_collected: fields.cashOnDelivery,
        customer_name: fields.customerName,
        customer_number: fields.customerPhoneNumber,
        order_id: fields.orderId,
        use_credit: Boolean(isWalletCreditUsed),
        captain_notes: fields.deliveryDetails,
        delivery_date: isScheduledDeliveriesEnabled ? selectedDeliveryTimeSlot?.date : '',
        delivery_instruction: fields.deliveryAction,
        delivery_time_slot:
          isScheduledDeliveriesEnabled && selectedDeliveryTimeSlot
            ? `${selectedDeliveryTimeSlot?.from} - ${selectedDeliveryTimeSlot?.to}`
            : '',
        delivery_type: selectedDeliveryTimeSlot ? DeliveryOption.SCHEDULED : DeliveryOption.INSTANT,
        dropoff_latitude: dropOffLocation?.location.lat,
        dropoff_longitude: dropOffLocation?.location.lng,
        pickup_latitude: pickupLocation?.location.lat,
        pickup_longitude: pickupLocation?.location.lng,
        fare_estimate: Number(estimatedPrice) || undefined
      });
    } finally {
      setIsFormSubmitting(false);
    }
  };

  const onFinishFailed = ({ values, errorFields }: ValidateErrorEntity<DeliveryFormValues>) => {
    publishAnalytikaEvent(requestDeliveryFormValidationFailed({ values, errorFields }));
  };

  const onCODChange = (value: number | null) => {
    //TODO: remove this if after rollout 100%
    if (value) {
      form.setFieldValue('deliveryAction', DeliveryAction.CALL_MERCHANT);
      setIsLeaveAtDoorstepDisabled(true);
    } else {
      setIsLeaveAtDoorstepDisabled(false);
    }
  };

  const isSubmitDisabled = isBlocked || isFormSubmitting || isDropoffSearchLoading;

  const handleClickAddNewAddress = () => {
    setPickupDropdownOpen(false);
    publishAnalytikaEvent(
      addNewAddressClicked(ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY, {
        pickup_location: selectedPickupLocationId,
        dropoff_location: selectedDropoffLocationId
      })
    );
    publishAV2Event({
      event_name: ANALYTIKA_EVENTS_V2.EXPR_CLICK_ADD_NEW_ADDRESS,
      event_version: 2,
      pick_up_location: pickupLocation?.nickname
    });
    setTimeout(() => setAddLocationModalVisible(true), 200);
  };

  const stepPickupFirst = (
    <Row>
      <Col span={24}>
        <Form.Item
          name="pickupLocation"
          label={t('deliveries.pickupLocation.label')}
          rules={[{ required: true, message: t('deliveries.pickupLocation.missing') }]}
        >
          <CustomCard
            title={t('deliveries.requestFirstDelivery.pickup.title')}
            content={t('deliveries.requestFirstDelivery.pickup.description')}
            buttonText={t('deliveries.requestFirstDelivery.pickup.buttonText')}
            onButtonClick={handleClickAddNewAddress}
          />
        </Form.Item>
      </Col>
    </Row>
  );

  const stepDropOffFirst = (
    <Row>
      <Col span={24}>
        <Form.Item
          name="dropOffLocation"
          rules={[{ required: isDropOffMandatory, message: t('deliveries.dropoffLocation.required') }]}
        >
          <CustomCard
            title={t('deliveries.requestFirstDelivery.dropoff.title')}
            content={t('deliveries.requestFirstDelivery.dropoff.description')}
            buttonText={t('deliveries.requestFirstDelivery.dropoff.buttonText')}
            onButtonClick={openAddDropoffModal}
            buttonDisabled={!pickupLocation}
          />
        </Form.Item>
      </Col>
    </Row>
  );

  const stepCheckoutFirst = (
    <Row>
      <Col span={24}>
        <CustomCard
          title={t('deliveries.requestFirstDelivery.checkout.title')}
          content={t('deliveries.requestFirstDelivery.checkout.description')}
          buttonText={t('deliveries.requestFirstDelivery.checkout.buttonText')}
          onButtonClick={openAddDropoffModal}
          buttonDisabled={!isDropoffValid}
        />
      </Col>
    </Row>
  );

  const stepPickupClassic = (
    <>
      <Row>
        <Col span={24}>
          <Form.Item
            name="pickupLocation"
            label={t('deliveries.pickupLocation.label')}
            rules={[
              { required: true, message: t('deliveries.pickupLocation.missing') },
              {
                required: true,
                validateTrigger: 'onChange',
                validator: deliveryZoneValidator
              }
            ]}
          >
            {isPickupLocationsLoading ? (
              <Skeleton.Input block active size={'default'} />
            ) : pickupLocation ? (
              <Select
                size={'large'}
                showSearch
                getPopupContainer={triggerNode => {
                  return triggerNode;
                }}
                disabled={isBlocked}
                placeholder={t('deliveries.pickupLocation.placeholder')}
                optionFilterProp="children"
                filterOption={true}
                open={isPickupDropdownOpen}
                onDropdownVisibleChange={visible => setPickupDropdownOpen(visible)}
                onChange={onChangePickupLocation}
                data-testid="pickupLocation"
                virtual={false}
                dropdownRender={menu => (
                  <>
                    {menu}
                    <Button
                      size="small"
                      type="link"
                      onClick={handleClickAddNewAddress}
                      className={styles.addNewAddressOption}
                    >
                      {t('deliveries.addNewAddressLink')}
                    </Button>
                  </>
                )}
              >
                {pickupLocationSelectOptions}
              </Select>
            ) : (
              <Button
                block
                size="large"
                disabled={isBlocked}
                icon={<PlusOutlined />}
                onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) => {
                  publishAnalytikaEvent(
                    addNewAddressClicked(ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY, {
                      pickup_location: selectedPickupLocationId,
                      dropoff_location: selectedDropoffLocationId
                    })
                  );
                  publishAV2Event({
                    event_name: ANALYTIKA_EVENTS_V2.EXPR_CLICK_ADD_NEW_ADDRESS,
                    event_version: 2,
                    pick_up_location: selectedPickupLocationId,
                    drop_off_location: selectedDropoffLocationId
                  });
                  setAddLocationModalVisible(true);
                  event.stopPropagation();
                }}
              >
                {t('deliveries.addNewAddressLink')}
              </Button>
            )}
          </Form.Item>
        </Col>
      </Row>
      <Row className={styles.rowFormItem}>
        <Col span={24} className={styles.limitedWidthColumn}>
          <Form.Item
            name="contactPhoneNumber"
            label={t('deliveries.contactPhoneNumber.label')}
            extra={t('deliveries.contactPhoneNumber.info')}
            validateTrigger="onBlur"
            rules={[
              {
                required: true,
                validator: phoneNumberValidator
              }
            ]}
          >
            {userContactNumber && (
              <PhoneNumberAntd
                data-testid="contactPhoneNumber"
                size={'large'}
                phoneNumberDefault={userContactNumber}
                disabled={isBlocked}
              />
            )}
          </Form.Item>
        </Col>
      </Row>
      <Row className={styles.rowFormItem}>
        <Col span={24} className={styles.limitedWidthColumn}>
          <Form.Item
            name="orderId"
            label={t('deliveries.orderId.label')}
            rules={[{ required: isOrderIdMandatory, message: t('deliveries.orderId.required') }]}
          >
            <Input
              size={'large'}
              disabled={isBlocked}
              maxLength={50}
              placeholder={t('deliveries.orderId.placeholder')}
              data-testid="orderId"
              autoComplete="nope"
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  const isCCODSelected =
    Form.useWatch<PaymentOnDeliveryType>('paymentMethodOnDelivery', form) === PaymentOnDeliveryType.CCOD;

  const stepCODClassic = (
    <Row className={styles.rowFormItem}>
      <Col span={24} className={styles.limitedWidthColumn}>
        <Form.Item
          name="cashOnDelivery"
          label={t(`deliveries.${isCCODAllowed ? 'creditCardOnDelivery' : 'cashOnDelivery'}.label`)}
          rules={[{ required: false }]}
          extra={!isCCODSelected && t('deliveries.cashOnDelivery.info')}
        >
          <InputNumber
            addonAfter={currency}
            onChange={onCODChange}
            size={'large'}
            style={{ width: '100%' }}
            maxLength={10}
            min={0}
            step={1}
            disabled={isBlocked}
            precision={fractionDigits}
            placeholder={t('deliveries.cashOnDelivery.placeholder')}
            data-testid="cashOnDelivery"
          />
        </Form.Item>
      </Col>
    </Row>
  );

  const stepCCODExtended = (
    <>
      <Row className={styles.rowFormItem}>
        <Col span={24} className={styles.limitedWidthColumn}>
          <Form.Item label={t('deliveries.isPaymentOnDeliveryRequired.label')}>
            <Switch
              checked={isPaymentOnDeliveryRequired}
              onClick={() => {
                form.resetFields(['paymentMethodOnDelivery', 'cashOnDelivery']);
                setIsPaymentOnDeliveryRequired(!isPaymentOnDeliveryRequired);
              }}
              checkedChildren={t('deliveries.isPaymentOnDeliveryRequired.yes')}
              unCheckedChildren={t('deliveries.isPaymentOnDeliveryRequired.no')}
            />
          </Form.Item>
        </Col>
      </Row>
      {isPaymentOnDeliveryRequired && (
        <>
          <Row className={styles.rowFormItem}>
            <Col span={24} className={styles.limitedWidthColumn}>
              <Form.Item
                name="paymentMethodOnDelivery"
                label={t(`deliveries.paymentMethodTypeOnDelivery.label`)}
                rules={[{ required: false }]}
              >
                <Radio.Group disabled={isBlocked} style={{ width: '100%' }} buttonStyle="solid">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Radio
                      value={PaymentOnDeliveryType.CASH}
                      className={styles.radioItem}
                      data-testid={`paymentOnDelivery_${PaymentOnDeliveryType.CASH}`}
                    >
                      <Space align="baseline">
                        <Icon color={colorIcon} size={14} component={() => <CashIcon />} />
                        {t('deliveries.paymentMethodTypeOnDelivery.cash')}
                      </Space>
                    </Radio>
                    <Radio
                      disabled={!isCCODAllowed}
                      value={PaymentOnDeliveryType.CCOD}
                      className={styles.radioItem}
                      data-testid={`paymentOnDelivery_${PaymentOnDeliveryType.CCOD}`}
                    >
                      <Space>
                        <Icon
                          size={14}
                          color={isCCODAllowed ? colorIcon : colorTextDisabled}
                          style={{ color: isCCODAllowed ? colorIcon : colorTextDisabled }}
                          component={() => <CreditCardIcon />}
                        />
                        <Typography.Text disabled={!isCCODAllowed}>
                          {t('deliveries.paymentMethodTypeOnDelivery.creditCard')}
                        </Typography.Text>
                      </Space>
                    </Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          {stepCODClassic}
        </>
      )}
    </>
  );

  const stepDropoffClassic = (
    <>
      {isCustomFulfillmentDistanceActive && (
        <Row>
          <Banner
            icon={<ExclamationCircleOutlined />}
            showIcon
            message={t('deliveries.bannerCustomFulfillment.description', {
              distance: customFulfillmentDistance,
              time: dayjs(cfdEndsAt).format('HH:mm (DD/MM)')
            })}
            type={'info'}
            className={styles.bannerWithoutDescription}
          />
        </Row>
      )}
      <Row>
        <Col span={24} className={selectedDropoffLocationId ? '' : styles.limitedWidthColumn}>
          <Form.Item
            name="dropOffLocation"
            rules={[{ required: isDropOffMandatory, message: t('deliveries.dropoffLocation.required') }]}
          >
            {selectedDropoffLocationId ? (
              <Card className={styles.dropOffLocationArea}>
                <Row>
                  <Col>
                    <Form.Item
                      label={
                        <Typography.Text type={'secondary'}>
                          {t('deliveries.dropoffLocation.staticLabel')}
                        </Typography.Text>
                      }
                    >
                      <Typography>
                        {dropOffLocation?.formattedAddress ||
                          `${dropOffLocation?.location?.lat}, ${dropOffLocation?.location?.lng}`}
                      </Typography>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={10} justify={'space-between'} align={'stretch'}>
                  <Col xs={24} lg={12} xxl={18}>
                    <Form.Item
                      name="buildingName"
                      label={<Typography.Text type={'secondary'}>{t('deliveries.buildingName.label')}</Typography.Text>}
                    >
                      <Typography> {usersBuildingName} </Typography>
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={12} xxl={6}>
                    <Button
                      block
                      type={'default'}
                      icon={<EditOutlined />}
                      size={'middle'}
                      onClick={openAddDropoffModal}
                      disabled={isBlocked}
                    >
                      {t('deliveries.dropoffLocation.buttonEditText')}
                    </Button>
                  </Col>
                </Row>
              </Card>
            ) : (
              <Button block size={'large'} icon={<PlusOutlined />} onClick={openAddDropoffModal} disabled={isBlocked}>
                {t('deliveries.dropoffLocation.buttonAddText')}
              </Button>
            )}
          </Form.Item>
        </Col>
      </Row>
      <Row className={styles.rowFormItem}>
        <Col span={24}>
          <Form.Item
            name="customerName"
            label={t('deliveries.customerName.required')}
            rules={[
              {
                required: selectedDeliveryOption === DeliveryOption.SCHEDULED,
                message: t('deliveries.customerName.required')
              }
            ]}
          >
            <Input
              disabled={isBlocked}
              size={'large'}
              maxLength={100}
              placeholder={t('deliveries.customerName.placeholder')}
              data-testid="customerName"
              autoComplete="nope"
            />
          </Form.Item>
        </Col>
      </Row>
      <Row className={styles.rowFormItem}>
        <Col span={24} className={styles.limitedWidthColumn}>
          <Form.Item
            name="customerPhoneNumber"
            label={t('deliveries.customerPhoneNumber.labelRequired')}
            extra={t('deliveries.customerPhoneNumber.info')}
            validateTrigger="onBlur"
            rules={[
              {
                required: isCustomerPhoneNumberMandatory || selectedDeliveryOption === DeliveryOption.SCHEDULED,
                validator: phoneNumberValidator
              }
            ]}
          >
            <PhoneNumberAntd
              data-testid="customerPhoneNumber"
              size={'large'}
              defaultCountryCode={isoCountryName}
              disabled={isBlocked}
            />
          </Form.Item>
        </Col>
      </Row>
      {isCCODEnabled ? stepCCODExtended : stepCODClassic}
      <Row className={styles.rowFormItem}>
        <Col span={24} className={styles.limitedWidthColumn}>
          <Form.Item name="deliveryAction" label={t('deliveries.doorstep.label')} rules={[{ required: false }]}>
            <Radio.Group disabled={isBlocked} style={{ width: '100%' }} buttonStyle="solid">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Radio
                  disabled={isLeaveAtDoorstepDisabled}
                  value={DeliveryAction.LEAVE_AT_DOOR}
                  className={styles.radioItem}
                  data-testid={`deliveryAction_${DeliveryAction.LEAVE_AT_DOOR}`}
                >
                  <Space>
                    <Icon color={colorIcon} size={14} component={() => <DoorstepIcon />} />
                    {t('deliveries.doorstep.LEAVE_AT_DOOR.optionName')}
                  </Space>
                  <Popover
                    title={t('deliveries.doorstep.LEAVE_AT_DOOR.popover.title')}
                    content={
                      <ul>
                        <li>{t('deliveries.doorstep.LEAVE_AT_DOOR.popover.firstPoint')}</li>
                        <li>{t('deliveries.doorstep.LEAVE_AT_DOOR.popover.secondPoint')}</li>
                      </ul>
                    }
                  >
                    <InfoCircleOutlined style={{ color: colorInfoBorder }} />
                  </Popover>
                </Radio>
                <Radio
                  value={DeliveryAction.CALL_MERCHANT}
                  className={styles.radioItem}
                  data-testid={`deliveryAction_${DeliveryAction.CALL_MERCHANT}`}
                >
                  <Space>
                    <Icon style={{ color: colorIcon }} component={() => <PhoneOutlined />} />
                    {t('deliveries.doorstep.CALL_MERCHANT.optionName')}
                  </Space>
                  <Popover
                    title={t('deliveries.doorstep.CALL_MERCHANT.popover.title')}
                    content={
                      <ul>
                        <li>{t('deliveries.doorstep.CALL_MERCHANT.popover.firstPoint')}</li>
                      </ul>
                    }
                  >
                    <InfoCircleOutlined style={{ color: colorInfoBorder }} />
                  </Popover>
                </Radio>
                <Radio
                  value={DeliveryAction.RETURN}
                  className={styles.radioItem}
                  data-testid={`deliveryAction_${DeliveryAction.RETURN}`}
                >
                  <Space>
                    <Icon style={{ color: colorIcon }} component={() => <RollbackOutlined />} />
                    {t('deliveries.doorstep.RETURN.optionName')}
                  </Space>
                  <Popover
                    title={t('deliveries.doorstep.RETURN.popover.title')}
                    content={
                      <ul>
                        <li>{t('deliveries.doorstep.RETURN.popover.firstPoint')}</li>
                        <li>{t('deliveries.doorstep.RETURN.popover.secondPoint')}</li>
                      </ul>
                    }
                  >
                    <InfoCircleOutlined style={{ color: colorInfoBorder }} />
                  </Popover>
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>
      <Row className={styles.rowFormItem}>
        <Col span={24}>
          <Form.Item name="deliveryDetails" label={t('deliveries.deliveryDetails.label')} rules={[{ required: false }]}>
            <Input.TextArea
              disabled={isBlocked}
              maxLength={390}
              placeholder={t('deliveries.deliveryDetails.placeholder')}
              data-testid="deliveryDetails"
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  const handleSelectDeliveryTypeClick = () => {
    setIsDrawerVisible(!isDrawerVisible);
    publishAV2Event({
      event_name: ANALYTIKA_EVENTS_V2.EXPR_CLICK_BUTTON,
      event_version: 3,
      button_name: 'edit_delivery_option',
      estimate: estimatedPrice,
      delivery_type: selectedDeliveryOption,
      delivery_date: selectedDeliveryTimeSlot?.date || '',
      delivery_time_slot: selectedDeliveryTimeSlot ? displayDeliveryTimeslotTime(selectedDeliveryTimeSlot) : ''
    });

    publishAV2Event({
      event_name: ANALYTIKA_EVENTS_V2.EXPR_VIEW_SCREEN,
      screen_name: ANALYTIKA_SCREENS.SHEDULED_SIDEBAR_OPEN,
      // TODO: double check event versions
      event_version: 1
    });
  };

  const isPickupZoneValidationFailedError =
    form.getFieldError('pickupLocation')?.[0] === t('deliveries.pickupLocation.zoneValidationFailed');

  const stepCheckoutClassic = (
    <Card className={styles.dropOffLocationArea}>
      {isScheduledDeliveriesEnabled ? (
        <Row data-testid="scheduled-delivery-section">
          <Col>
            <Form.Item
              label={t('deliveries.selectDeliveryOption.label')}
              required
              requiredMark
              validateStatus={isPickupZoneValidationFailedError ? 'error' : undefined}
              rules={[{ required: true }]}
              help={isPickupZoneValidationFailedError ? t('deliveries.pickupLocation.zoneValidationFailed') : undefined}
              extra={
                !isPickupZoneValidationFailedError
                  ? selectedDeliveryTimeSlot
                    ? `${t('deliveries.selectDeliveryOption.dropoff')} ${displayDeliveryTimeslotTime(selectedDeliveryTimeSlot)} ${displayDeliveryTimeslotDate(selectedDeliveryTimeSlot.date)}`
                    : t('deliveries.selectDeliveryOption.description')
                  : undefined
              }
            >
              <Space.Compact>
                <Input
                  readOnly
                  onClick={handleSelectDeliveryTypeClick}
                  style={{ cursor: 'pointer' }}
                  placeholder={t('deliveries.selectDeliveryOption.placeholder')}
                  value={t(`deliveries.selectDeliveryOption.${selectedDeliveryOption}`)}
                />

                <Button
                  danger={isPickupZoneValidationFailedError}
                  onClick={handleSelectDeliveryTypeClick}
                  type="default"
                >
                  <PencilIcon />
                </Button>
              </Space.Compact>
            </Form.Item>
          </Col>
        </Row>
      ) : null}
      {isEstimatesEnabled ? (
        <Row data-testid="estimate-price-section">
          <Col>
            <Statistic
              loading={isPriceEstimationLoading}
              valueRender={value => (
                <Typography.Title level={2}>
                  {value} {estimatedPrice.currency || currency}
                </Typography.Title>
              )}
              valueStyle={{
                fontSize: '20px !important'
              }}
              title={
                <span>
                  <Typography>
                    {t('deliveries.checkout.estimatedPriceLabel')}{' '}
                    <Popover
                      title={t('deliveries.checkout.popover.title')}
                      content={t('deliveries.checkout.popover.content')}
                    >
                      <InfoCircleOutlined style={{ fontSize: 16, color: colorInfoBorder }} />
                    </Popover>
                  </Typography>{' '}
                </span>
              }
              value={estimatedPrice.currency ? estimatedPrice.tripCost : '----'}
              precision={fractionDigits}
            />
          </Col>
        </Row>
      ) : null}
    </Card>
  );

  const stepsBaseItems = [
    {
      title: (
        <Space className={styles.stepTitle}>
          {!isMdBreakpoint && <Icon style={{ fontSize: 18 }} component={() => <ShopOutlined />} />}
          <Typography.Title level={3}>{t('deliveries.pickupSectionTitle')}</Typography.Title>
        </Space>
      ),
      icon: <ShopOutlined style={pickupLocation ? iconActiveRoundConfig : iconDisabledRoundConfig} />,
      className: styles.singleStep,
      description: isFirstDeliveryScreen && !pickupLocation ? stepPickupFirst : stepPickupClassic
    },
    {
      title: (
        <Space className={styles.stepTitle}>
          {!isMdBreakpoint && <Icon style={{ fontSize: 18, color: 'black' }} component={() => <FlagOutlined />} />}
          <Typography.Title level={3}>{t('deliveries.dropOffSectionTitle')}</Typography.Title>{' '}
        </Space>
      ),
      className: styles.singleStep,
      icon: <FlagOutlined style={isDropoffValid ? iconActiveRoundConfig : iconDisabledRoundConfig} />,
      description: isFirstDeliveryScreen && !selectedDropoffLocationId ? stepDropOffFirst : stepDropoffClassic
    }
  ];

  //TODO: switch step to 2 when estimated price is calculated
  const checkoutStepItem = {
    title: (
      <Space className={styles.stepTitle}>
        {!isMdBreakpoint && (
          <Icon style={{ fontSize: 18, color: 'black' }} component={() => <DollarCircleOutlined />} />
        )}
        <Typography.Title level={3}>{t('deliveries.checkoutSectionTitle')}</Typography.Title>{' '}
      </Space>
    ),
    className: styles.singleStep,
    icon: <DollarCircleOutlined style={isDropoffValid ? iconActiveRoundConfig : iconDisabledRoundConfig} />,
    description: isFirstDeliveryScreen && !paymentMethods.length ? stepCheckoutFirst : stepCheckoutClassic
  };

  const fetchTimeslotsFunction = useCallback(async () => {
    if (dropOffLocation?.location) {
      await fetchTimeslots(dropOffLocation?.location);
      // TODO: is it even possible to have a dropoff location without a location?
    }
  }, [fetchTimeslots, dropOffLocation]);

  const confirmScheduledChangesDrawer = ({
    deliveryOption,
    deliverySlot
  }: {
    deliverySlot?: TimeSlotWithDate;
    deliveryOption: DeliveryOption;
  }) => {
    form.setFieldsValue({ deliveryTimeSlot: deliverySlot });
    setSelectedDeliveryOption(deliveryOption);
    setIsDrawerVisible(false);
    form.validateFields(['pickupLocation']);
  };

  useEffect(() => {
    setTimeout(() => form.validateFields(['pickupLocation']), 500);
  }, [isPickupLocationsLoading]);

  return (
    <Paper>
      <Form
        initialValues={formInitValues}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        form={form}
        layout="vertical"
        data-testid="CreateDeliveryForm"
      >
        <Row gutter={[24, 0]}>
          <Col xs={24} md={16}>
            <Steps
              direction="vertical"
              current={isDropoffValid ? (isScheduledDeliveriesEnabled ? 2 : 1) : 0}
              items={
                isEstimatesEnabled || isScheduledDeliveriesEnabled
                  ? [...stepsBaseItems, checkoutStepItem]
                  : stepsBaseItems
              }
            />
          </Col>
          <Col xs={24} md={8}>
            <div className={styles.mapContainer}>
              <Map
                mapUseCase={MapUseCase.RequestDelivery}
                useDetailedIcons={true}
                pickup={pickupLocation}
                dropOff={dropOffLocation}
                useExternalDirections
                externalDirections={drivingDirections}
                onDropOffMarkerChange={onDropOffMarkerChange}
                mapDefaultCenter={userCity ? { lat: userCity?.lat, lng: userCity?.lng } : null}
                overlay={!pickupLocation ? overlay : ''}
              />
            </div>
          </Col>
        </Row>
        <Row className={styles.rowFormItem}>
          <Col flex={1}>
            <div className={styles.submitButtonSection}>
              <Button
                data-testid="submitButton"
                block={!isSmBreakpoint}
                type="primary"
                size={'large'}
                htmlType="submit"
                disabled={isSubmitDisabled}
              >
                {t('deliveries.createDeliveryButton')}
              </Button>
            </div>
          </Col>
        </Row>
      </Form>

      {isAddLocationModalVisible && (
        <AddLocationModal
          visible={isAddLocationModalVisible}
          onClose={() => setAddLocationModalVisible(false)}
          existingAddresses={pickupLocations}
        />
      )}
      {pickupLocation && isAddDropOffModalVisible && (
        <AddDropoffModal
          pickupLocation={pickupLocation}
          visible={isAddDropOffModalVisible}
          onClose={() => setAddDropOffModalVisible(false)}
          dropoffLocationSelectOptions={dropoffLocationSelectOptions}
          isDropoffSearchLoading={isDropoffSearchLoading}
          onDropoffSearchStart={onDropoffSearchStart}
          onSubmitModal={onSubmitModal}
          preSelectedDropoffId={selectedDropoffLocationId}
          usersBuildingName={usersBuildingName}
          dropOffLocations={dropOffLocations}
        />
      )}
      {isDrawerVisible && (
        <DeliveryOptionDrawer
          hasDropoff={Boolean(dropOffLocation)}
          currentTimeSlots={timeSlotsByDate}
          isTimeSlotsFetching={isTimeSlotsFetching}
          fetchTimeSlots={fetchTimeslotsFunction}
          onClose={() => setIsDrawerVisible(false)}
          isVisible={isDrawerVisible}
          confirmDeliveryOption={confirmScheduledChangesDrawer}
          currentDeliveryOption={selectedDeliveryOption}
          currentDeliverySlot={selectedDeliveryTimeSlot}
        />
      )}
    </Paper>
  );
};

export default CreateDeliveryFormv2;
